/**
 * Mixpanel API Client
 * Fetches analytics data via backend proxy to keep credentials secure
 */

import { env } from '@/lib/env';
import { getAuthItem } from '@/lib/authStorage';
import { getActiveWorkspaceId } from '@/lib/api/workspace-context';

export interface MixpanelConfig {
  apiSecret: string;
  projectId: string;
  // Indicates if Service Account credentials are stored on the server
  hasServiceAccount?: boolean;
}

export interface MixpanelEvent {
  event: string;
  properties: Record<string, unknown>;
}

export interface AnalyticsSummary {
  // Core metrics
  dau: number;
  mau: number;
  stickiness: number; // DAU/MAU ratio
  totalEvents: number;
  uniqueUsers: number;

  // Retention
  retentionD1: number;
  retentionD7: number;
  retentionD30: number;

  // Trends (daily values for sparklines)
  dailyActiveUsers: number[];
  dailyEvents: number[];

  // Breakdowns
  topEvents: Array<{ name: string; count: number }>;
  platforms: Array<{ name: string; count: number }>;

  // Metadata
  dateRange: { from: string; to: string };
  lastUpdated: string;
}

// Get auth token from auth storage
function getAuthToken(): string | null {
  return getAuthItem('xavior_auth_token');
}

// Parse JSONL response from Mixpanel Export API
function parseJSONL(text: string): MixpanelEvent[] {
  const events: MixpanelEvent[] = [];
  if (!text.trim()) return events;

  for (const line of text.trim().split('\n')) {
    if (line) {
      try {
        events.push(JSON.parse(line));
      } catch {
        // Skip malformed lines
      }
    }
  }
  return events;
}

// Format date as YYYY-MM-DD
function formatDate(date: Date): string {
  return date.toISOString().split('T')[0];
}

// Get date N days ago
function daysAgo(n: number): Date {
  const date = new Date();
  date.setDate(date.getDate() - n);
  return date;
}

/**
 * Fetch raw events via backend proxy
 * Credentials are sent to our backend, not directly to Mixpanel
 */
export async function fetchEvents(
  config: MixpanelConfig,
  fromDate: string,
  toDate: string
): Promise<MixpanelEvent[]> {
  const token = getAuthToken();

  const response = await fetch(`${env.apiBaseUrl}/workspaces/${getActiveWorkspaceId()}/plugins/mixpanel/export`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      apiSecret: config.apiSecret,
      projectId: config.projectId,
      fromDate,
      toDate,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `API error: ${response.status}`);
  }

  const text = await response.text();
  return parseJSONL(text);
}

/**
 * Calculate analytics summary from raw events
 */
export function calculateSummary(
  events: MixpanelEvent[],
  fromDate: string,
  toDate: string
): AnalyticsSummary {
  // User activity by date
  const usersByDate = new Map<string, Set<string>>();
  const eventsByDate = new Map<string, number>();
  const allUsers = new Set<string>();
  const eventCounts = new Map<string, number>();
  const platformCounts = new Map<string, number>();

  // User first seen dates (for retention)
  const userFirstSeen = new Map<string, string>();
  const userActiveDates = new Map<string, Set<string>>();

  for (const event of events) {
    const props = event.properties;
    const userId = (props.distinct_id || props.$user_id) as string;
    const timestamp = props.time as number;
    const eventName = event.event;
    const platform = props.$os as string;

    if (!userId || !timestamp) continue;

    const date = new Date(timestamp * 1000).toISOString().split('T')[0];
    allUsers.add(userId);

    // Track user by date
    if (!usersByDate.has(date)) {
      usersByDate.set(date, new Set());
    }
    usersByDate.get(date)!.add(userId);

    // Track events by date
    eventsByDate.set(date, (eventsByDate.get(date) || 0) + 1);

    // Track event names
    eventCounts.set(eventName, (eventCounts.get(eventName) || 0) + 1);

    // Track platforms
    if (platform) {
      platformCounts.set(platform, (platformCounts.get(platform) || 0) + 1);
    }

    // Track user first seen
    if (!userFirstSeen.has(userId) || date < userFirstSeen.get(userId)!) {
      userFirstSeen.set(userId, date);
    }

    // Track user active dates
    if (!userActiveDates.has(userId)) {
      userActiveDates.set(userId, new Set());
    }
    userActiveDates.get(userId)!.add(date);
  }

  // Calculate DAU (average daily active users)
  const dauValues = Array.from(usersByDate.values()).map((users) => users.size);
  const avgDau = dauValues.length > 0
    ? dauValues.reduce((a, b) => a + b, 0) / dauValues.length
    : 0;

  // Calculate MAU (unique users in last 30 days)
  const mau = allUsers.size;

  // Calculate stickiness
  const stickiness = mau > 0 ? (avgDau / mau) * 100 : 0;

  // Calculate retention
  const calculateRetention = (dayOffset: number): number => {
    let retained = 0;
    let total = 0;

    for (const [userId, firstDate] of userFirstSeen.entries()) {
      const targetDate = new Date(firstDate);
      targetDate.setDate(targetDate.getDate() + dayOffset);
      const targetDateStr = targetDate.toISOString().split('T')[0];

      // Only count users whose retention window falls within our data
      if (targetDateStr <= toDate) {
        total++;
        if (userActiveDates.get(userId)?.has(targetDateStr)) {
          retained++;
        }
      }
    }

    return total > 0 ? (retained / total) * 100 : 0;
  };

  // Build daily arrays for sparklines (sorted by date)
  const sortedDates = Array.from(usersByDate.keys()).sort();
  const dailyActiveUsers = sortedDates.map((date) => usersByDate.get(date)?.size || 0);
  const dailyEvents = sortedDates.map((date) => eventsByDate.get(date) || 0);

  // Top events
  const topEvents = Array.from(eventCounts.entries())
    .filter(([name]) => !name.startsWith('$')) // Filter system events
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .map(([name, count]) => ({ name, count }));

  // Platforms
  const platforms = Array.from(platformCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([name, count]) => ({ name, count }));

  return {
    dau: Math.round(avgDau),
    mau,
    stickiness: Math.round(stickiness * 10) / 10,
    totalEvents: events.length,
    uniqueUsers: allUsers.size,
    retentionD1: Math.round(calculateRetention(1) * 10) / 10,
    retentionD7: Math.round(calculateRetention(7) * 10) / 10,
    retentionD30: Math.round(calculateRetention(30) * 10) / 10,
    dailyActiveUsers,
    dailyEvents,
    topEvents,
    platforms,
    dateRange: { from: fromDate, to: toDate },
    lastUpdated: new Date().toISOString(),
  };
}

/**
 * Fetch and calculate analytics summary
 */
export async function fetchAnalyticsSummary(
  config: MixpanelConfig,
  days: number = 30
): Promise<AnalyticsSummary> {
  // Use yesterday as toDate to avoid timezone issues with Mixpanel
  const toDate = formatDate(daysAgo(1));
  const fromDate = formatDate(daysAgo(days + 1));

  const events = await fetchEvents(config, fromDate, toDate);
  return calculateSummary(events, fromDate, toDate);
}

/**
 * Validate Mixpanel credentials via backend proxy
 * Returns detailed error information for better UX
 */
export async function validateCredentials(
  config: MixpanelConfig
): Promise<{ valid: boolean; error?: string }> {
  const token = getAuthToken();

  try {
    const response = await fetch(`${env.apiBaseUrl}/workspaces/${getActiveWorkspaceId()}/plugins/mixpanel/validate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        apiSecret: config.apiSecret,
        projectId: config.projectId,
      }),
    });

    if (!response.ok) {
      return { valid: false, error: `Server error: ${response.status}` };
    }

    const result = await response.json();
    return { valid: result.valid, error: result.error };
  } catch (err) {
    return {
      valid: false,
      error: err instanceof Error ? err.message : 'Connection failed',
    };
  }
}

// ============================================
// Segmentation API (requires Service Account)
// ============================================

export interface SegmentationQuery {
  event: string;
  fromDate: string;
  toDate: string;
  type?: 'general' | 'unique' | 'average'; // default: unique
  on?: string; // property to segment by, e.g., 'properties["mp_country_code"]'
}

export interface SegmentationResult {
  series: string[]; // dates
  values: Record<string, Record<string, number>>; // { eventName: { date: count } }
}

function getSeriesValues(result: SegmentationResult, key: string): Record<string, number> {
  if (result.values[key]) {
    return result.values[key];
  }
  const firstKey = Object.keys(result.values)[0];
  return firstKey ? (result.values[firstKey] || {}) : {};
}

/**
 * Check if config has Service Account credentials
 */
export function hasServiceAccount(config: MixpanelConfig): boolean {
  return Boolean(config.hasServiceAccount);
}

/**
 * Fetch aggregated data via Segmentation API (requires Service Account)
 * Returns server-side aggregated data - works for full year without downloading raw events
 */
export async function fetchSegmentation(
  instanceId: string,
  query: SegmentationQuery
): Promise<SegmentationResult> {
  const token = getAuthToken();

  if (!instanceId) {
    throw new Error('Plugin instance ID is required for Segmentation API');
  }

  const response = await fetch(`${env.apiBaseUrl}/workspaces/${getActiveWorkspaceId()}/plugins/instances/${instanceId}/mixpanel/segmentation`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      event: query.event,
      fromDate: query.fromDate,
      toDate: query.toDate,
      type: query.type || 'unique',
      on: query.on,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `API error: ${response.status}`);
  }

  const result = await response.json();
  return {
    series: result.data.series,
    values: result.data.values,
  };
}

/**
 * Get top N days for a metric using Segmentation API
 */
export async function getTopDays(
  instanceId: string,
  event: string,
  fromDate: string,
  toDate: string,
  limit: number = 10
): Promise<Array<{ date: string; count: number }>> {
  const result = await fetchSegmentation(instanceId, {
    event,
    fromDate,
    toDate,
    type: 'unique',
  });

  // Flatten and sort by count descending
  const eventValues = getSeriesValues(result, event);
  const sorted = Object.entries(eventValues)
    .map(([date, count]) => ({ date, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);

  return sorted;
}

/**
 * Get sessions/events by geographic dimension
 */
export async function getByGeography(
  instanceId: string,
  event: string,
  fromDate: string,
  toDate: string,
  dimension: 'country' | 'city' | 'region' = 'country'
): Promise<Array<{ name: string; count: number }>> {
  const propMap: Record<string, string> = {
    country: 'properties["mp_country_code"]',
    city: 'properties["$city"]',
    region: 'properties["$region"]',
  };

  const result = await fetchSegmentation(instanceId, {
    event,
    fromDate,
    toDate,
    type: 'unique',
    on: propMap[dimension],
  });

  // Aggregate across all dates
  const totals = new Map<string, number>();
  for (const [geo, dateValues] of Object.entries(result.values)) {
    const total = Object.values(dateValues as Record<string, number>).reduce((a, b) => a + b, 0);
    totals.set(geo, total);
  }

  return Array.from(totals.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
}

// ============================================
// Anomaly Detection
// ============================================

export interface AnomalyResult {
  date: string;
  value: number;
  zscore: number;
  type: 'spike' | 'drop';
}

export interface AnomalyAnalysis {
  anomalies: AnomalyResult[];
  mean: number;
  stdDev: number;
  dailyValues: Array<{ date: string; value: number }>;
}

/**
 * Detect anomalies in daily metrics using z-score
 * Returns days where value deviates significantly from mean
 */
export async function detectAnomalies(
  instanceId: string,
  event: string,
  fromDate: string,
  toDate: string,
  sensitivity: number = 2 // Z-score threshold (2 = ~95% confidence)
): Promise<AnomalyAnalysis> {
  const result = await fetchSegmentation(instanceId, {
    event,
    fromDate,
    toDate,
    type: 'unique',
  });

  // Extract daily values
  const eventValues = getSeriesValues(result, event);
  const dailyValues = Object.entries(eventValues)
    .map(([date, value]) => ({ date, value }))
    .sort((a, b) => a.date.localeCompare(b.date));

  if (dailyValues.length < 3) {
    return { anomalies: [], mean: 0, stdDev: 0, dailyValues };
  }

  // Calculate mean
  const values = dailyValues.map(d => d.value);
  const mean = values.reduce((a, b) => a + b, 0) / values.length;

  // Calculate standard deviation
  const squaredDiffs = values.map(v => Math.pow(v - mean, 2));
  const avgSquaredDiff = squaredDiffs.reduce((a, b) => a + b, 0) / values.length;
  const stdDev = Math.sqrt(avgSquaredDiff);

  // Find anomalies (values outside threshold)
  const anomalies: AnomalyResult[] = [];

  if (stdDev > 0) {
    for (const { date, value } of dailyValues) {
      const zscore = (value - mean) / stdDev;
      if (Math.abs(zscore) >= sensitivity) {
        anomalies.push({
          date,
          value,
          zscore: Math.round(zscore * 100) / 100,
          type: zscore > 0 ? 'spike' : 'drop',
        });
      }
    }
  }

  // Sort by absolute z-score (most anomalous first)
  anomalies.sort((a, b) => Math.abs(b.zscore) - Math.abs(a.zscore));

  return {
    anomalies,
    mean: Math.round(mean),
    stdDev: Math.round(stdDev),
    dailyValues,
  };
}

// ============================================
// Top Users (requires Export API)
// ============================================

export interface TopUser {
  rank: number;
  userId: string;
  eventCount: number;
  daysActive: number;
}

/**
 * Get top N users by activity
 * Uses Export API (limited to 60-90 days of data)
 */
export async function getTopUsers(
  config: MixpanelConfig,
  fromDate: string,
  toDate: string,
  limit: number = 10
): Promise<TopUser[]> {
  // Fetch raw events via Export API
  const token = getAuthToken();

  const response = await fetch(`${env.apiBaseUrl}/workspaces/${getActiveWorkspaceId()}/plugins/mixpanel/export`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      apiSecret: config.apiSecret,
      projectId: config.projectId,
      fromDate,
      toDate,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `API error: ${response.status}`);
  }

  const text = await response.text();

  // Parse JSONL and aggregate by user
  const userStats = new Map<string, { eventCount: number; dates: Set<string> }>();

  for (const line of text.trim().split('\n')) {
    if (!line) continue;
    try {
      const event = JSON.parse(line);
      const userId = event.properties?.distinct_id || event.properties?.$user_id;
      const timestamp = event.properties?.time;

      if (!userId) continue;

      if (!userStats.has(userId)) {
        userStats.set(userId, { eventCount: 0, dates: new Set() });
      }

      const stats = userStats.get(userId)!;
      stats.eventCount++;

      if (timestamp) {
        const date = new Date(timestamp * 1000).toISOString().split('T')[0];
        stats.dates.add(date);
      }
    } catch {
      // Skip malformed lines
    }
  }

  // Convert to array and sort by event count
  const sorted = Array.from(userStats.entries())
    .map(([userId, stats]) => ({
      userId,
      eventCount: stats.eventCount,
      daysActive: stats.dates.size,
    }))
    .sort((a, b) => b.eventCount - a.eventCount)
    .slice(0, limit);

  // Add rank
  return sorted.map((user, index) => ({
    rank: index + 1,
    ...user,
  }));
}

// ============================================
// AI Insights (respects Core/External setting)
// ============================================

export interface AIInsight {
  type: 'opportunity' | 'warning' | 'observation';
  title: string;
  description: string;
  action?: string;
}

export interface AIInsightsResult {
  insights: AIInsight[];
  summary: string;
  generatedAt: string;
  mode: 'local' | 'api';
}

export interface AIAnswerResult {
  answer: string;
  generatedAt: string;
  mode: 'local' | 'api';
}

/**
 * Get the current chat mode from localStorage
 * 'local' = Core (private), 'api' = External
 */
function getChatMode(): 'local' | 'api' {
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem('chat-mode');
    if (stored === 'local' || stored === 'api') {
      return stored;
    }
  }
  return 'local'; // Default to Core for privacy
}

async function readSSEStream(response: Response): Promise<string> {
  const text = await response.text();
  const payloads: string[] = [];

  // First pass: standard SSE lines
  for (const line of text.split(/\r?\n/)) {
    if (line.startsWith('data:')) {
      payloads.push(line.slice(5).trimStart());
    }
  }

  // Second pass: handle providers that emit `event: ... data: ...` on the same line
  if (payloads.length === 0 && text.includes('data:')) {
    for (const segment of text.split('data: ').slice(1)) {
      const newlineIdx = segment.search(/\r|\n/);
      const eventIdx = segment.indexOf('event:');
      let end = segment.length;
      if (eventIdx !== -1 && eventIdx < end) {
        end = eventIdx;
      }
      if (newlineIdx !== -1 && newlineIdx < end) {
        end = newlineIdx;
      }
      const chunk = segment.slice(0, end).trim();
      if (chunk) {
        payloads.push(chunk);
      }
    }
  }

  if (payloads.length > 0) {
    return payloads.join('\n');
  }

  const jsonMatch = text.match(/\{[\s\S]*\}/);
  if (jsonMatch) {
    return jsonMatch[0];
  }

  return text;
}

function extractJsonPayload(text: string): string | null {
  const candidates: Array<{ raw: string; parsed: any }> = [];
  let inString = false;
  let escape = false;
  let depth = 0;
  let start = -1;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];

    if (char === '\\' && !escape) {
      escape = true;
      continue;
    }

    if (char === '"' && !escape) {
      inString = !inString;
    }

    escape = false;

    if (inString) {
      continue;
    }

    if (char === '{') {
      if (depth === 0) {
        start = i;
      }
      depth++;
    } else if (char === '}' && depth > 0) {
      depth--;
      if (depth === 0 && start !== -1) {
        candidates.push(text.slice(start, i + 1));
        start = -1;
      }
    }
  }

  for (const candidate of candidates) {
    const trimmed = candidate.raw.trim();
    if (!trimmed) continue;
    try {
      candidate.parsed = JSON.parse(trimmed);
      candidate.raw = trimmed;
    } catch {
      candidate.parsed = null;
    }
  }

  for (let i = candidates.length - 1; i >= 0; i--) {
    const candidate = candidates[i];
    const data = candidate.parsed;
    if (data && typeof data === 'object') {
      if (Array.isArray(data.insights) && 'summary' in data) {
        return candidate.raw;
      }
      if (Array.isArray(data.insights)) {
        return candidate.raw;
      }
    }
  }

  for (let i = candidates.length - 1; i >= 0; i--) {
    if (candidates[i].parsed) {
      return candidates[i].raw;
    }
  }

  return null;
}

/**
 * Generate AI-driven insights from analytics data
 * Respects user's Core/External chat preference
 */
export async function generateInsights(
  summary: AnalyticsSummary,
  recentAnomalies?: AnomalyAnalysis
): Promise<AIInsightsResult> {
  const token = getAuthToken();
  const mode = getChatMode();

  // Format analytics data as context for LLM
  const analyticsContext = `
Analytics Summary (${summary.dateRange.from} to ${summary.dateRange.to}):
- DAU: ${summary.dau}, MAU: ${summary.mau}, Stickiness: ${summary.stickiness}%
- Retention: D1=${summary.retentionD1}%, D7=${summary.retentionD7}%, D30=${summary.retentionD30}%
- Total Events: ${summary.totalEvents}, Unique Users: ${summary.uniqueUsers}
- Top Events: ${summary.topEvents.slice(0, 5).map(e => `${e.name} (${e.count})`).join(', ')}
- Platforms: ${summary.platforms.map(p => `${p.name} (${p.count})`).join(', ')}
${recentAnomalies?.anomalies.length ? `\nRecent Anomalies: ${recentAnomalies.anomalies.slice(0, 3).map(a => `${a.date}: ${a.type} (${a.zscore}σ)`).join(', ')}` : ''}
`;

  const prompt = `Based on this mobile app analytics data, provide 3-5 actionable insights. For each insight, specify:
- Type: "opportunity", "warning", or "observation"
- Title: Brief headline (5-10 words)
- Description: One sentence explanation
- Action: Optional recommended action

Focus on retention, engagement, and growth opportunities. Be specific and data-driven.

${analyticsContext}

Respond ONLY with valid JSON in this exact format:
{
  "summary": "One sentence overall assessment",
  "insights": [
    {"type": "opportunity", "title": "...", "description": "...", "action": "..."},
    {"type": "warning", "title": "...", "description": "..."}
  ]
}`;

  const response = await fetch(`${env.chatBaseUrl}/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Workspace-Id': getActiveWorkspaceId(),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      messages: [{ role: 'user', content: prompt }],
      mode,
      temperature: 0.3,
      maxTokens: 1000,
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to generate insights');
  }

  const responseContent = await readSSEStream(response);

  // Parse JSON from response
  let parsed: { summary: string; insights: AIInsight[] };
  try {
    const jsonPayload = extractJsonPayload(responseContent);
    if (!jsonPayload) {
      throw new Error('No JSON found in response');
    }
    parsed = JSON.parse(jsonPayload);
  } catch (err) {
    parsed = {
      summary: 'Unable to parse AI response',
      insights: [{
        type: 'observation',
        title: 'AI analysis unavailable',
        description: responseContent.slice(0, 200) || (err instanceof Error ? err.message : 'No response received'),
      }],
    };
  }

  return {
    insights: parsed.insights || [],
    summary: parsed.summary || '',
    generatedAt: new Date().toISOString(),
    mode,
  };
}

/**
 * Conversational analytics answer (used by InsightChat follow-ups)
 */
export async function answerAnalyticsQuestion(
  summary: AnalyticsSummary,
  question: string,
  options?: { context?: string }
): Promise<AIAnswerResult> {
  const token = getAuthToken();
  const mode = getChatMode();

  const analyticsContext = `
Analytics Summary (${summary.dateRange.from} to ${summary.dateRange.to}):
- DAU: ${summary.dau}, MAU: ${summary.mau}, Stickiness: ${summary.stickiness}%
- Retention: D1=${summary.retentionD1}%, D7=${summary.retentionD7}%, D30=${summary.retentionD30}%
- Total Events: ${summary.totalEvents}, Unique Users: ${summary.uniqueUsers}
- Top Events: ${summary.topEvents.slice(0, 5).map((event) => `${event.name} (${event.count})`).join(', ')}
- Platforms: ${summary.platforms.map((platform) => `${platform.name} (${platform.count})`).join(', ')}
`;

  const prompt = `You are a senior product strategist. Use the analytics summary and any additional context to answer the question with clear reasoning and actionable recommendations.\n\n${analyticsContext}\nAdditional context: ${options?.context || 'None provided.'}\n\nQuestion: ${question}\n\nRespond with a concise paragraph (or short list) that directly addresses the question.`;

  const response = await fetch(`${env.chatBaseUrl}/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Workspace-Id': getActiveWorkspaceId(),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      messages: [{ role: 'user', content: prompt }],
      mode,
      temperature: 0.4,
      maxTokens: 800,
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to answer question');
  }

  const answer = (await readSSEStream(response)).trim();

  return {
    answer,
    generatedAt: new Date().toISOString(),
    mode,
  };
}
